import {
  Banner,
  Box,
  Button,
  Flex,
  MegaphoneIcon,
  Popover,
  Typography,
  useToast,
} from '@iheartjane/reefer';
import { Form } from '@iheartjane/reefer-hook-form';
import { useMaskito } from '@maskito/react';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';

import type { UpsertBloomMenuTemplateArgs } from '@jane/business-admin/data-access';
import {
  useFetchBloomMenuTemplate,
  useFetchCurrentBloomMenu,
  useUpsertBloomMenuTemplate,
} from '@jane/business-admin/data-access';
import { businessPaths, urlMask } from '@jane/business-admin/util';
import { normalizeUrl } from '@jane/shared/util';

import { ModalPageWrapper } from './components/ModalPageWrapper';

export const PremiumFooter = () => {
  const navigate = useNavigate();
  const urlRef = useMaskito({ options: urlMask });
  const toast = useToast();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [clickedAction, setClickedAction] = useState<'publish' | 'reimport'>();
  const bloomMenuQuery = useFetchCurrentBloomMenu();
  const bloomMenuTemplate = useFetchBloomMenuTemplate('footer');
  const { mutate: updateBloomMenuTemplate, isLoading } =
    useUpsertBloomMenuTemplate({
      menuId: bloomMenuQuery.data?.id,
      templateType: 'footer',
    });
  const formMethods = useForm<UpsertBloomMenuTemplateArgs>({
    defaultValues: {
      source_content_selector:
        bloomMenuTemplate.data?.source_content_selector ?? 'footer',
      source_url: bloomMenuTemplate.data?.source_url,
    },
    reValidateMode: 'onChange',
    mode: 'all',
  });
  const {
    reset,
    formState: { isDirty },
  } = formMethods;

  useEffect(() => {
    if (bloomMenuTemplate.data) {
      reset({
        source_content_selector:
          bloomMenuTemplate.data?.source_content_selector ?? 'footer',
        source_url: bloomMenuTemplate.data?.source_url ?? 'https://',
      });
    }
  }, [bloomMenuTemplate.data]);

  const importFooter = useCallback(
    ({ source_content_selector, source_url }: UpsertBloomMenuTemplateArgs) => {
      setErrorMessage(null);
      updateBloomMenuTemplate(
        {
          source_content_selector,
          source_url: normalizeUrl(source_url),
          template_type: 'footer',
        },
        {
          onSuccess: () => {
            toast.add({
              label: 'Footer imported!',
              variant: 'success',
            });
            reset({ source_content_selector, source_url });
            navigate(businessPaths.premium());
          },
          onError: (error: unknown) => {
            setErrorMessage(
              error instanceof Error && error.message
                ? error.message
                : 'An error occurred.'
            );
          },
        }
      );
    },
    [toast, updateBloomMenuTemplate, navigate, reset]
  );

  const publishFooter = useCallback(
    (args: UpsertBloomMenuTemplateArgs) => {
      setClickedAction('publish');
      importFooter(args);
    },
    [importFooter]
  );

  const reimportFooter = useCallback(() => {
    setClickedAction('reimport');
    void formMethods.handleSubmit(importFooter)();
  }, [formMethods, importFooter]);

  return (
    <ModalPageWrapper
      formMethods={formMethods}
      isLoading={isLoading && clickedAction === 'publish'}
      onSubmit={publishFooter}
      secondaryAction={
        <Popover
          alignment={{ horizontal: 'right', vertical: 'bottom' }}
          label="Re-import footer"
          openOn="hover"
          target={
            <Button
              ml="auto"
              mr={16}
              label="Re-import footer"
              type="button"
              variant="secondary"
              onClick={reimportFooter}
              disabled={isDirty}
              loading={isLoading && clickedAction === 'reimport'}
            />
          }
        >
          <Popover.Content>
            <Typography>
              Refreshes the current footer from your source URL.
            </Typography>
          </Popover.Content>
        </Popover>
      }
      title="Footer"
    >
      {errorMessage && (
        <Box m={24}>
          <Banner
            full
            label={errorMessage}
            onDismiss={() => setErrorMessage(null)}
            variant="error"
          />
        </Box>
      )}
      <Flex
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        py={24}
      >
        <Flex
          borderRadius="sm"
          background="grays-ultralight"
          p={16}
          maxWidth={600}
          alignItems="center"
        >
          <Box mr={16}>
            <MegaphoneIcon size="md" />
          </Box>
          <Typography variant="body-bold">
            To import your website's footer, please contact your Partner Success
            rep. They will assist you with the technical setup. Once your footer
            is imported, your website's favicon will automatically be included
            as well.
          </Typography>
        </Flex>

        <Flex alignItems="center" flexDirection="column" maxWidth={450} my={32}>
          <Form.TextField
            label="Source"
            helperText="Provide the URL of your footer website to directly import the footer"
            mb={24}
            name="source_url"
            endIcon={<Typography color="grays-mid">URL</Typography>}
            ref={urlRef}
            required
          />
          <Form.TextField
            label="Content selector"
            helperText="Provide the CSS content selector of the footer element to import"
            endIcon={<Typography color="grays-mid">Selector</Typography>}
            name="source_content_selector"
            required
          />
        </Flex>
      </Flex>
    </ModalPageWrapper>
  );
};
